import React, { useState, useEffect } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const GeometryBuilderGame = ({ gameData, onComplete }) => {
    const [currentShapeIndex, setCurrentShapeIndex] = useState(0);
    const [selectedPieces, setSelectedPieces] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const [score, setScore] = useState(0);
    const [shuffledPieces, setShuffledPieces] = useState([]);

    const currentShape = gameData?.shapes?.[currentShapeIndex];

    useEffect(() => {
        if (currentShape) {
            const pieces = currentShape.all_pieces || currentShape.pieces || [];
            setShuffledPieces([...pieces].sort(() => Math.random() - 0.5));
            setSelectedPieces([]);
        }
    }, [currentShape]);

    const togglePiece = (pieceId) => {
        if (feedback) return;
        triggerHaptic(30);

        if (selectedPieces.includes(pieceId)) {
            setSelectedPieces(prev => prev.filter(id => id !== pieceId));
        } else {
            setSelectedPieces(prev => [...prev, pieceId]);
        }
    };

    const checkAnswer = () => {
        if (feedback || selectedPieces.length === 0) return;
        const requiredIds = currentShape.required_piece_ids || (currentShape.pieces ? currentShape.pieces.map(p => p.id) : []);
        
        const hasAllRequired = requiredIds.every(id => selectedPieces.includes(id));
        const hasNoExtras = selectedPieces.every(id => requiredIds.includes(id));
        const isCorrect = hasAllRequired && hasNoExtras;

        if (isCorrect) {
            setFeedback('correct');
            const points = gameData.points_per_shape || 30;
            const newScore = score + points;
            setScore(newScore);
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                if (currentShapeIndex + 1 >= gameData.shapes.length) {
                    triggerHaptic([200, 100, 200, 100, 300]);
                    onComplete?.(newScore);
                } else {
                    setCurrentShapeIndex(prev => prev + 1);
                    setFeedback(null);
                }
            }, 4000);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => setFeedback(null), 1500);
        }
    };

    const resetSelection = () => {
        if (feedback) return;
        setSelectedPieces([]);
        triggerHaptic(50);
    };

    if (!gameData || !currentShape) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-purple-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">📐</div>
                <h3 className="text-base md:text-3xl font-black text-purple-900 tracking-tighter uppercase">Konstruojami brėžiniai...</h3>
            </div>
        );
    }

    const getPieceShape = (type) => {
        switch (type) {
            case 'small_triangle': return '▲';
            case 'square': return '■';
            case 'parallelogram': return '▱';
            default: return '●';
        }
    };

    const progress = (currentShapeIndex / gameData.shapes.length) * 100;
    const requiredCount = currentShape.required_piece_ids?.length || currentShape.pieces?.length || 0;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-purple-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-purple-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        📐
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Geometrijos Konstruktorius
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Tikslumas</p>
                        <p className="text-sm md:text-2xl font-black text-purple-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Konstrukcija</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentShapeIndex + 1}/{gameData.shapes.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-purple-400 via-pink-500 to-rose-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(168,85,247,0.3)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-12 text-center border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center h-full justify-center shadow-2xl">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-6xl md:text-[150px] opacity-[0.03] select-none pointer-events-none">📐</div>
                <h4 className="text-[8px] md:text-[10px] font-black text-purple-400 uppercase tracking-[0.5em] mb-6 md:mb-10 text-center">Projektas Braižykloje</h4>
                
                <div className="space-y-4 md:space-y-8 relative z-10 w-full flex flex-col items-center">
                    <div className="text-7xl md:text-[12rem] mb-4 drop-shadow-2xl animate-float">
                        {currentShape.emoji}
                    </div>
                    <div className="space-y-4 flex flex-col items-center">
                        <h3 className="text-lg md:text-4xl font-black text-slate-800 tracking-tighter uppercase leading-none">{currentShape.name}</h3>
                        <div className="bg-purple-900 text-purple-100 p-4 md:p-8 rounded-2xl md:rounded-[3rem] border-4 border-white shadow-xl relative overflow-hidden max-w-lg">
                            <p className="text-sm md:text-xl font-bold italic font-serif leading-relaxed">
                                "{currentShape.hint}"
                            </p>
                        </div>
                        <div className="flex items-center justify-center gap-2">
                            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                            <span className="text-[10px] md:text-sm font-black text-slate-400 uppercase tracking-widest">Reikia lygiai {requiredCount} detalių</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-6 flex flex-col items-center w-full max-w-4xl mx-auto py-4 md:py-8">
            <div className="flex items-center justify-between w-full px-4 mb-2">
                <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                    <h4 className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">Konstravimo Aikštelė</h4>
                </div>
                <div className="bg-slate-800/80 px-4 py-1.5 md:px-6 md:py-2 rounded-full border border-slate-700 shadow-xl">
                    <span className={`text-sm md:text-2xl font-black font-mono ${selectedPieces.length === requiredCount ? 'text-emerald-400 animate-pulse' : 'text-white'}`}>
                        {selectedPieces.length} / {requiredCount}
                    </span>
                </div>
            </div>
            
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 shadow-2xl border-white/60 bg-slate-800 relative overflow-hidden flex flex-col min-h-[450px] w-full justify-between">
                <div className="absolute top-0 right-0 p-4 text-6xl opacity-[0.05] select-none pointer-events-none text-white">🧩</div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-6 relative z-10 p-2 md:p-4">
                    {shuffledPieces.map((piece) => {
                        const isSelected = selectedPieces.includes(piece.id);
                        return (
                            <button
                                key={piece.id}
                                onClick={() => togglePiece(piece.id)}
                                disabled={!!feedback}
                                className={`
                                    relative aspect-square rounded-xl md:rounded-[2.5rem] flex flex-col items-center justify-center transition-all duration-300 transform border-b-8 md:border-b-[12px]
                                    ${isSelected 
                                        ? 'scale-105 shadow-[0_0_40px_rgba(255,255,255,0.15)] brightness-125 -translate-y-1 border-b-white/20' 
                                        : 'bg-slate-600 border-slate-500 hover:scale-[1.02] hover:bg-slate-500 active:translate-y-1 active:border-b-0'}
                                    disabled:opacity-40
                                `}
                                style={{
                                    backgroundColor: isSelected ? (piece.color || '#8B5CF6') : undefined,
                                    borderColor: isSelected ? 'rgba(255,255,255,0.3)' : undefined
                                }}
                            >
                                <div className={`text-4xl md:text-8xl transition-all duration-500 drop-shadow-lg ${isSelected ? 'scale-125 text-white' : 'text-slate-200'}`}>
                                    {getPieceShape(piece.type)}
                                </div>
                                {isSelected && (
                                    <div className="absolute top-2 right-2 w-6 h-6 md:w-10 md:h-10 bg-white text-emerald-600 rounded-full flex items-center justify-center text-xs md:text-xl shadow-xl border-2 md:border-4 border-emerald-100 animate-pop-in">✓</div>
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className="flex gap-2 relative z-10 pt-8">
                    <button
                        onClick={resetSelection}
                        disabled={selectedPieces.length === 0 || !!feedback}
                        className="flex-1 py-3 md:py-6 bg-slate-800 text-slate-400 rounded-xl md:rounded-2xl font-black text-xs md:text-xl border-b-4 md:border-b-8 border-slate-700 hover:bg-slate-700 active:translate-y-1 active:border-b-0 transition-all disabled:opacity-10 uppercase tracking-widest shadow-lg"
                    >
                        TRINTI 🔄
                    </button>
                    <button
                        onClick={checkAnswer}
                        disabled={selectedPieces.length === 0 || !!feedback}
                        className={`
                            flex-[2] py-3 md:py-6 px-4 md:px-12 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl md:rounded-2xl font-black text-xs md:text-2xl shadow-xl transition-all border-b-4 md:border-b-8 border-purple-800 uppercase tracking-[0.2em]
                            ${selectedPieces.length === requiredCount ? 'scale-105 active:translate-y-1 active:border-b-0' : 'opacity-20'}
                        `}
                    >
                        STATYTI ✓
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-6 md:p-12 rounded-[2.5rem] md:rounded-[4rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-5xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback === 'correct' ? '📐' : '🧩'}
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className={`text-2xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'STATINYS BAIGTAS!' : 'NETIKSLUS PLANAS!'}
                            </h2>

                            <div className={`p-4 md:p-8 rounded-2xl border-2 italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-sm md:text-xl font-bold text-slate-700 text-center leading-relaxed">
                                    {feedback === 'correct' ? 'Puiku! Geometrinę konstrukciją surinkai nepriekaištingai!' : 'Oi! Panašu, kad pasirinktos detalės neatitinka brėžinio.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default GeometryBuilderGame;


